import React from "react"

import { usePluginMetadata } from "../../../recoil/pluginStudio/hooks"

interface FileBreadcrumbsUIProps {
  path: string
  pluginName?: string
}

/**
 * Presentation component for file breadcrumbs
 */
export const FileBreadcrumbsUI: React.FC<FileBreadcrumbsUIProps> = ({
  path,
  pluginName,
}) => {
  // Skip empty path
  if (!path) {
    return (
      <div className="flex items-center px-4 h-full bg-gray-800 text-gray-300">
        <span className="text-gray-500">No file selected</span>
      </div>
    )
  }

  // Parse path into segments
  const segments = path.split("/").filter(Boolean)
  const fileName = segments.length > 0 ? segments[segments.length - 1] : ""

  // Handle plugin name display - if pluginName is provided, use it directly
  const displayPluginName = pluginName
    ? pluginName
    : segments.length > 0
      ? segments[0]
      : "Unknown Plugin"

  return (
    <div className="flex items-center px-4 h-full bg-gray-800 text-gray-300 overflow-x-auto whitespace-nowrap">
      <div className="flex items-center">
        {/* Plugin icon and name */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-blue-400 mr-1.5"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 6a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2zm0 6a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2z"
            clipRule="evenodd"
          />
        </svg>
        <span className="font-medium text-gray-200">{displayPluginName}</span>

        {/* Path segments */}
        {segments.length > 1 && (
          <>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mx-1 text-gray-500"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>

            {segments.slice(1, -1).map((segment, index) => (
              <React.Fragment key={index}>
                <span className="text-gray-400">{segment}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mx-1 text-gray-500"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </React.Fragment>
            ))}

            {/* File name */}
            {segments.length > 1 && (
              <div className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-1 text-gray-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="font-medium text-white">{fileName}</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

/**
 * Container component that connects to Recoil state
 */
const FileBreadcrumbs: React.FC = () => {
  const { activePlugin, activeFileMetadata } = usePluginMetadata()

  if (!activeFileMetadata || !activePlugin) {
    return <FileBreadcrumbsUI path="" />
  }

  return (
    <FileBreadcrumbsUI
      path={
        activeFileMetadata.path ||
        `/${activePlugin.id}/${activeFileMetadata.name}`
      }
      pluginName={activePlugin.manifest?.name || activePlugin.name}
    />
  )
}

export default FileBreadcrumbs
